<script setup lang="ts">
import type { StyleValue } from 'vue'
import { computed } from 'vue'

import type { UiAvatarProps } from './types.ts'

const props = withDefaults(defineProps<UiAvatarProps>(), {
  square: false,
  round: false,
  mask: false,
})

const LIGHTEN = 130

const BACKGROUND_COLORS = [
  '#F44336',
  '#FF4081',
  '#9C27B0',
  '#673AB7',
  '#3F51B5',
  '#2196F3',
  '#03A9F4',
  '#00BCD4',
  '#009688',
  '#4CAF50',
  '#8BC34A',
  '#ADB93A',
  /* '#FFEB3B' , */ '#FFC107',
  '#FF9800',
  '#FF5722',
  '#795548',
  '#9E9E9E',
  '#607D8B',
]

const background = computed<string>(() => randomBackgroundColor(props.spacedName.length, BACKGROUND_COLORS))

const fontColor = computed<string>(() => lightenColor(background.value, LIGHTEN))

const style = computed<StyleValue>(() => ({
  userSelect: 'none' as const,
  backgroundColor: background.value,
  color: fontColor.value,
}))

const userInitial = computed<string>(() => getInitials(props.spacedName))

function getInitials(spacedName: string): string {
  const parts = spacedName.split(/[ -]/)
  let initials = ''

  for (let i = 0; i < parts.length; i++) {
    initials += parts[i].charAt(0)
  }

  if (initials.length > 3 && initials.search(/[A-Z]/) !== -1) {
    initials = initials.replace(/[a-z]+/g, '')
  }

  initials = initials.substring(0, 3).toUpperCase()

  return initials
}

function randomBackgroundColor(seed: number, colors: string[]): string {
  return colors[seed % colors.length]
}

function lightenColor(hex: string, amt: number): string {
  let hexLocal = hex
  // From https://css-tricks.com/snippets/javascript/lighten-darken-color/
  let usePound = false

  if (hexLocal[0] === '#') {
    hexLocal = hexLocal.slice(1)
    usePound = true
  }

  const num = parseInt(hexLocal, 16)
  let r = (num >> 16) + amt

  if (r > 255) r = 255
  else if (r < 0) r = 0

  let b = ((num >> 8) & 0x00ff) + amt

  if (b > 255) b = 255
  else if (b < 0) b = 0

  let g = (num & 0x0000ff) + amt

  if (g > 255) g = 255
  else if (g < 0) g = 0

  return (usePound ? '#' : '') + String('000000' + (g | (b << 8) | (r << 16)).toString(16)).slice(-6)
}
</script>

<template>
  <div
    aria-hidden="true"
    class="flex items-center justify-center"
    :class="{
      'rounded-full': !mask && round,
      'rounded-sm': !mask && !round && !square,
      'mask mask-squircle': mask && !square,
    }"
    :style="style"
  >
    <span class="text-shadow font-semibold">{{ userInitial }}</span>
  </div>
</template>
